import { useCallback, useEffect, useState } from 'react';
import {
  planningDecisionIntelApi,
  type DecisionDashboard,
  type DecisionGraph,
  type LearningRecord,
  type PlanningAgent,
  type PlanningDecision,
  type PlanningRecommendation,
} from '../../api/planningdecisionintel';

type Tab = 'dashboard' | 'inbox' | 'evidence' | 'graph' | 'approvals' | 'learning' | 'history' | 'agents';
type Theme = 'light' | 'dark';

function fmtMoney(n: number) {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

function priorityColor(p: string) {
  if (p === 'critical') return 'text-red-500';
  if (p === 'high') return 'text-orange-500';
  if (p === 'medium') return 'text-amber-500';
  return 'text-slate-500';
}

export function PlanningDecisionIntelPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('dashboard');
  const [dashboard, setDashboard] = useState<DecisionDashboard | null>(null);
  const [recommendations, setRecommendations] = useState<PlanningRecommendation[]>([]);
  const [decisions, setDecisions] = useState<PlanningDecision[]>([]);
  const [graph, setGraph] = useState<DecisionGraph | null>(null);
  const [learning, setLearning] = useState<LearningRecord[]>([]);
  const [history, setHistory] = useState<PlanningRecommendation[]>([]);
  const [agents, setAgents] = useState<PlanningAgent[]>([]);
  const [selected, setSelected] = useState<PlanningRecommendation | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, recs, decs, g, lr, hist, ag] = await Promise.all([
        planningDecisionIntelApi.dashboard(),
        planningDecisionIntelApi.listRecommendations(),
        planningDecisionIntelApi.listDecisions(),
        planningDecisionIntelApi.decisionGraph(),
        planningDecisionIntelApi.learning(),
        planningDecisionIntelApi.decisionHistory(),
        planningDecisionIntelApi.listAgents(),
      ]);
      setDashboard(dash);
      setRecommendations(recs);
      setDecisions(decs);
      setGraph(g);
      setLearning(lr);
      setHistory(hist);
      setAgents(ag);
      if (recs.length && !selected) setSelected(recs[0]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load decision intelligence');
    }
  }, [selected]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleReview = async (id: string) => {
    setActionMsg(null);
    try {
      await planningDecisionIntelApi.reviewRecommendation(id, 'Reviewed in Decision Intelligence Center');
      setActionMsg('Recommendation reviewed');
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Review failed');
    }
  };

  const handleApprove = async (id: string) => {
    setActionMsg(null);
    try {
      await planningDecisionIntelApi.approveRecommendation(id);
      setActionMsg('Recommendation approved');
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Approval failed');
    }
  };

  const handleReject = async (id: string) => {
    setActionMsg(null);
    try {
      await planningDecisionIntelApi.rejectRecommendation(id, 'Rejected by planner — requires alternative');
      setActionMsg('Recommendation rejected');
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Rejection failed');
    }
  };

  const handleExecute = async (id: string) => {
    setActionMsg(null);
    try {
      await planningDecisionIntelApi.executeRecommendation(id);
      setActionMsg('Recommendation executed');
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Execution failed');
    }
  };

  const shell = dark ? 'min-h-screen bg-slate-950 text-slate-100' : 'min-h-screen bg-slate-50 text-slate-900';
  const card = dark ? 'rounded-xl border border-slate-800 bg-slate-900 p-5' : 'rounded-xl border border-slate-200 bg-white p-5 shadow-sm';
  const header = dark ? 'border-b border-slate-800 bg-slate-900' : 'border-b border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-600';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'dashboard', label: 'Decision Center' },
    { id: 'inbox', label: 'Recommendation Inbox' },
    { id: 'evidence', label: 'Evidence Explorer' },
    { id: 'graph', label: 'Decision Graph' },
    { id: 'approvals', label: 'Approval Workspace' },
    { id: 'learning', label: 'Learning Dashboard' },
    { id: 'history', label: 'Decision History' },
    { id: 'agents', label: 'Multi-Agent Catalog' },
  ];

  return (
    <div className={shell}>
      <div className={`${header} px-6 py-4`}>
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold">Enterprise Planning Decision Intelligence</h1>
            <p className={`mt-1 text-sm ${muted}`}>
              Multi-agent reasoning, explainable recommendations, governed approvals, and enterprise decision memory
            </p>
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg px-3 py-2 text-sm ${dark ? 'bg-slate-800' : 'bg-slate-100'}`} aria-label="Toggle dark mode">{dark ? 'Light' : 'Dark'}</button>
            <button type="button" onClick={refresh} className="rounded-lg bg-violet-600 px-4 py-2 text-sm font-medium text-white hover:bg-violet-700">Refresh</button>
          </div>
        </div>
        {dashboard && (
          <p className={`mt-2 text-sm ${muted}`}>
            Decision Health {dashboard.enterprise_decision_health.toFixed(0)} · {dashboard.total_recommendations} recommendations · {dashboard.active_agents} agents
          </p>
        )}
      </div>

      <div className="border-b px-6">
        <nav className="-mb-px flex flex-wrap gap-4 overflow-x-auto" aria-label="Decision intelligence tabs">
          {tabs.map((t) => (
            <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`whitespace-nowrap border-b-2 px-1 py-3 text-sm font-medium ${tab === t.id ? 'border-violet-500 text-violet-600' : `border-transparent ${muted}`}`}>{t.label}</button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {error && <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
        {actionMsg && <div className="mb-4 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{actionMsg}</div>}

        {tab === 'dashboard' && dashboard && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Decision Quality Index', value: dashboard.decision_quality_index.toFixed(0) },
              { label: 'Acceptance Rate', value: `${(dashboard.decision_acceptance_rate * 100).toFixed(0)}%` },
              { label: 'Recommendation Accuracy', value: `${(dashboard.recommendation_accuracy * 100).toFixed(0)}%` },
              { label: 'Enterprise Decision Health', value: dashboard.enterprise_decision_health.toFixed(0) },
              { label: 'Pending Review', value: String(dashboard.pending_review) },
              { label: 'Pending Approval', value: String(dashboard.pending_approval) },
              { label: 'Cycle Time (hrs)', value: dashboard.decision_cycle_time_hours.toFixed(1) },
              { label: 'Recommendation ROI', value: `${dashboard.recommendation_roi.toFixed(1)}x` },
            ].map((kpi) => (
              <div key={kpi.label} className={card}>
                <p className={`text-xs uppercase tracking-wide ${muted}`}>{kpi.label}</p>
                <p className="mt-2 text-2xl font-semibold">{kpi.value}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'inbox' && (
          <div className="space-y-3">
            {recommendations.map((r) => (
              <div key={r.id} className={`${card} cursor-pointer ${selected?.id === r.id ? 'ring-2 ring-violet-500' : ''}`} onClick={() => setSelected(r)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setSelected(r)}>
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div>
                    <span className={`text-xs font-semibold uppercase ${priorityColor(r.priority)}`}>{r.priority}</span>
                    <h3 className="mt-1 font-medium">{r.title}</h3>
                    <p className={`mt-1 text-sm ${muted}`}>{r.source_module} · {r.agent_type.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="text-right text-sm">
                    <p>Confidence {(r.confidence * 100).toFixed(0)}%</p>
                    <p className={muted}>{r.approval_status}</p>
                  </div>
                </div>
                <p className={`mt-2 text-sm ${muted}`}>{r.reasoning_summary}</p>
                {r.expected_revenue_impact > 0 && <p className="mt-1 text-sm text-emerald-600">Revenue impact {fmtMoney(r.expected_revenue_impact)}</p>}
              </div>
            ))}
          </div>
        )}

        {tab === 'evidence' && selected && (
          <div className={card}>
            <h2 className="text-lg font-semibold">{selected.title}</h2>
            <p className={`mt-2 ${muted}`}>{selected.business_rationale}</p>
            <h3 className="mt-4 font-medium">Supporting Evidence</h3>
            <ul className="mt-2 space-y-2">
              {selected.evidence.map((e, i) => (
                <li key={i} className={`rounded-lg p-3 ${dark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                  <span className="font-medium">{e.source}</span> — {e.description}
                  <span className={`ml-2 text-sm ${muted}`}>({(e.confidence * 100).toFixed(0)}% confidence)</span>
                </li>
              ))}
            </ul>
            <h3 className="mt-4 font-medium">Alternatives & Trade-offs</h3>
            <ul className="mt-2 list-disc pl-5 text-sm">
              {selected.alternative_options.map((a, i) => <li key={i}>{a}</li>)}
            </ul>
            {selected.tradeoffs && <p className={`mt-2 text-sm ${muted}`}>{selected.tradeoffs}</p>}
            <h3 className="mt-4 font-medium">Known Risks</h3>
            <ul className="mt-2 list-disc pl-5 text-sm">
              {selected.known_risks.map((r, i) => <li key={i}>{r}</li>)}
            </ul>
          </div>
        )}

        {tab === 'graph' && graph && (
          <div className={card}>
            <h2 className="text-lg font-semibold">Enterprise Decision Graph</h2>
            <p className={`mt-1 text-sm ${muted}`}>{graph.nodes.length} nodes · {graph.edges.length} dependencies</p>
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {graph.nodes.map((n) => (
                <div key={n.id} className={`rounded-lg p-3 ${dark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                  <p className="font-medium">{n.label}</p>
                  <p className={`text-xs ${muted}`}>{n.module} · {n.node_type}</p>
                  <p className="text-sm">Confidence {(n.confidence * 100).toFixed(0)}%</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === 'approvals' && (
          <div className="space-y-4">
            {recommendations.filter((r) => r.approval_status !== 'executed' && r.approval_status !== 'rejected').slice(0, 5).map((r) => (
              <div key={r.id} className={card}>
                <h3 className="font-medium">{r.title}</h3>
                <p className={`mt-1 text-sm ${muted}`}>Approval path: {r.approval_path} · Status: {r.approval_status}</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" onClick={() => handleReview(r.id)} className="rounded-lg bg-slate-600 px-3 py-1.5 text-sm text-white">Review</button>
                  <button type="button" onClick={() => handleApprove(r.id)} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-sm text-white">Approve</button>
                  <button type="button" onClick={() => handleReject(r.id)} className="rounded-lg bg-red-600 px-3 py-1.5 text-sm text-white">Reject</button>
                  <button type="button" onClick={() => handleExecute(r.id)} className="rounded-lg bg-violet-600 px-3 py-1.5 text-sm text-white">Execute</button>
                </div>
              </div>
            ))}
            {decisions.length > 0 && (
              <div className={card}>
                <h3 className="font-medium">Cross-Functional Decisions</h3>
                <ul className="mt-2 space-y-2">
                  {decisions.map((d) => (
                    <li key={d.id} className={`text-sm ${muted}`}>{d.title} — {d.decision_owner} ({d.status})</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {tab === 'learning' && (
          <div className="space-y-3">
            {learning.map((l) => (
              <div key={l.id} className={card}>
                <p className="font-medium">{l.feedback_type.replace(/_/g, ' ')}</p>
                <p className={`text-sm ${muted}`}>Recommendation {l.recommendation_id}</p>
                {l.rejection_reason && <p className="mt-1 text-sm">Rejection: {l.rejection_reason}</p>}
                {l.business_outcome && <p className="mt-1 text-sm">Outcome: {l.business_outcome}</p>}
                {l.model_improvement && <p className="mt-1 text-sm text-violet-600">{l.model_improvement}</p>}
                <p className={`mt-1 text-xs ${muted}`}>Score {(l.outcome_score * 100).toFixed(0)}%</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'history' && (
          <div className="space-y-3">
            {history.length === 0 && <p className={muted}>No completed decisions yet — approve or reject recommendations to build history.</p>}
            {history.map((r) => (
              <div key={r.id} className={card}>
                <h3 className="font-medium">{r.title}</h3>
                <p className={`text-sm ${muted}`}>{r.approval_status} · {r.execution_status}</p>
              </div>
            ))}
          </div>
        )}

        {tab === 'agents' && (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {agents.map((a) => (
              <div key={a.type} className={card}>
                <h3 className="font-medium">{a.name}</h3>
                <p className={`text-xs uppercase ${muted}`}>{a.type.replace(/_/g, ' ')}</p>
                <p className="mt-2 text-sm">Confidence {(a.confidence * 100).toFixed(0)}%</p>
                <ul className={`mt-2 list-disc pl-4 text-xs ${muted}`}>
                  {a.responsibilities.slice(0, 3).map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
